
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Progress } from '@/components/ui/progress';
import { Brain, Search, Zap, ChevronRight } from 'lucide-react';

interface TypingIndicatorProps {
  isSearching?: boolean;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ isSearching = false }) => {
  const { t, language } = useLanguage();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [showArrow, setShowArrow] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let timeoutIds: NodeJS.Timeout[] = [];
    
    // Total animation duration: 13 seconds
    // Step 1: 0-3.5 seconds (Thinking)
    setCurrentStep(1);
    setShowArrow(false);
    
    // Arrow 1: 3.5-4 seconds
    timeoutIds.push(setTimeout(() => {
      setShowArrow(true);
    }, 3500));
    
    // Step 2: 4-8 seconds (Gathering Data)
    timeoutIds.push(setTimeout(() => {
      setCurrentStep(2);
      setShowArrow(false);
    }, 4000));
    
    // Arrow 2: 8.5-9 seconds
    timeoutIds.push(setTimeout(() => {
      setShowArrow(true);
    }, 8500));
    
    // Step 3: 9-13 seconds (Crafting Answer)
    timeoutIds.push(setTimeout(() => {
      setCurrentStep(3);
      setShowArrow(false);
    }, 9000));

    // Progress bar animation over 13 seconds
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const increment = 100 / (13 * 10); // 13 seconds, 10 updates per second
        return Math.min(prev + increment, 100);
      });
    }, 100);

    // Loop back progress every 3 seconds after reaching 100%
    const loopbackTimeout = setTimeout(() => {
      const loopbackInterval = setInterval(() => {
        setProgress(prev => {
          // Go back ~15% of progress
          const decrementAmount = 15;
          return Math.max(prev - decrementAmount, 85); // Don't go below 85%
        });
        
        // After going back, start progressing again over 3 seconds
        setTimeout(() => {
          const catchupInterval = setInterval(() => {
            setProgress(prev => {
              const increment = 15 / (3 * 10); // Catch up 15% over 3 seconds
              return Math.min(prev + increment, 100);
            });
          }, 100);
          
          timeoutIds.push(setTimeout(() => clearInterval(catchupInterval), 3000));
        }, 100);
      }, 3000);
      
      timeoutIds.push(loopbackInterval);
    }, 13000);

    return () => {
      timeoutIds.forEach(clearTimeout);
      clearInterval(progressInterval);
    };
  }, []);

  const getStepIcon = (step: number) => {
    switch (step) {
      case 1: return Brain;
      case 2: return Search;
      case 3: return Zap;
      default: return Brain;
    }
  };

  const getStepText = (step: number) => {
    switch (step) {
      case 1: return t('chat.agent.step1');
      case 2: return t('chat.agent.step2');
      case 3: return t('chat.agent.step3');
      default: return t('chat.agent.step1');
    }
  };

  return (
    <div className="flex justify-start animate-fade-in" data-typing-indicator="true">
      <div className="flex items-start space-x-2 max-w-[85%]">
        <div className="flex flex-col items-start w-full">
          {/* Agent Name and Current Step */}
          <div className="flex items-center space-x-3 mb-3">
            <span className="text-sm font-semibold text-foreground">{t('chat.agent.name')}</span>
            <div className="flex items-center space-x-2 px-3 py-1 bg-primary/5 rounded-full border border-primary/10">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-primary">
                {getStepText(currentStep)}
              </span>
            </div>
          </div>
          
          {/* Main Typing Bubble */}
          <div className="bg-gradient-to-br from-secondary/80 to-secondary/60 backdrop-blur-sm rounded-2xl rounded-bl-md px-6 py-4 shadow-lg border border-border/20 w-full max-w-md">
            {/* Icons Progress Visualization */}
            <div className="flex items-center justify-center space-x-3 mb-4">
              {[1, 2, 3].map((step, index) => {
                const Icon = getStepIcon(step);
                const isActive = currentStep === step && !showArrow;
                const isCompleted = step < currentStep;
                const isArrowVisible = showArrow && (
                  (currentStep === 1 && index === 0) || 
                  (currentStep === 2 && index === 1)
                );
                
                return (
                  <div key={step} className="flex items-center">
                    <div className={`relative flex items-center justify-center w-8 h-8 rounded-lg border-2 transition-all duration-500 ${
                      isActive 
                        ? 'bg-primary/20 border-primary shadow-lg animate-[pulse_3.25s_cubic-bezier(0.4,0,0.6,1)_infinite]' 
                        : isCompleted 
                          ? 'bg-primary/10 border-primary/30' 
                          : 'bg-muted/30 border-muted-foreground/20'
                    }`}>
                      <Icon 
                        size={16} 
                        className={`transition-all duration-500 ${
                          isActive 
                             ? 'text-primary animate-[pulse_3.25s_cubic-bezier(0.4,0,0.6,1)_infinite]'
                            : isCompleted 
                              ? 'text-primary/60' 
                              : 'text-muted-foreground/40'
                        }`} 
                      />
                      
                      {/* Glowing effect for active step */}
                      {isActive && (
                        <div className="absolute inset-0 rounded-lg bg-primary/10 animate-ping"></div>
                      )}
                    </div>
                    
                    {/* Arrow between steps */}
                    {index < 2 && (
                      <div className={`transition-all duration-300 ${
                        isArrowVisible ? 'opacity-100 scale-100' : 'opacity-30 scale-75'
                      }`}>
                        <ChevronRight 
                          size={12} 
                          className={`transition-all duration-300 ${
                            isArrowVisible 
                              ? 'text-primary animate-[pulse_3.25s_cubic-bezier(0.4,0,0.6,1)_infinite]' 
                              : 'text-muted-foreground/30'
                          }`} 
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Typing Animation Dots */}
            <div className="flex justify-center space-x-1 mb-4">
              <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            
            {/* Enhanced Progress Bar */}
            <div className="relative">
              <Progress 
                value={progress} 
                className="h-2 w-full bg-muted/40 shadow-inner" 
              />
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-primary/10 to-transparent opacity-60 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypingIndicator;
